import { inject, Injectable } from '@angular/core';
import { RegisterRequest } from './requests/register-request';
import { AuthResponse } from './store/auth-actions';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { VerificationRequest } from './requests/verification-request';
import { ResendTokenRequest } from './requests/resent-token-request';
import { VerificationCodeRequest } from './requests/verification-code-request';
import { ResetCheckRequest } from './requests/pass-reset-check';
import { PasswordResetRequest } from './requests/password-reset-request';
import { PasswordResetForm } from './requests/password-reset';
import { LocalStorageService } from 'app/storage/local-storage-service';
import { GraphQlService } from '@shared/services/graphql-service';
import { CHANGE_PASSWORD } from './graphql/auth-queries';
import { ChangePasswordDtoInput } from './requests/change-password.request';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl = `${environment.BASE_API_URL}/${environment.UAA_SERVICE}/api/anonymous/`;
  private localStorageService = inject(LocalStorageService);
  private http = inject(HttpClient);
  private graphQlService = inject(GraphQlService);

  private jsonHeaders() {
    return {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  }

  register(body: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}register`, body, this.jsonHeaders());
  }

  verifyToken(body: VerificationRequest): Observable<AuthResponse> {
    const endPoint =
      this.localStorageService.getVerificationEndPoint() === null ||
      this.localStorageService.getVerificationEndPoint() === ''
        ? 'otp/verify'
        : 'password-reset/otp/verify';
    return this.http.post<AuthResponse>(`${this.baseUrl}${endPoint}`, body, this.jsonHeaders());
  }

  verifyCode(body: VerificationCodeRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}email/verify`, body, this.jsonHeaders());
  }

  resendToken(body: ResendTokenRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}otp/resend`, body, this.jsonHeaders());
  }

  passwordResetCheck(body: ResetCheckRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${this.baseUrl}password-reset/check`,
      body,
      this.jsonHeaders()
    );
  }

  passwordResetLink(body: PasswordResetRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${this.baseUrl}password-reset/request`,
      body,
      this.jsonHeaders()
    );
  }

  resetPasswordConfirm(body: PasswordResetForm): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${this.baseUrl}password-reset/confirm`,
      body,
      this.jsonHeaders()
    );
  }

  changePassword(body: ChangePasswordDtoInput): Observable<any> {
    return this.graphQlService.mutate({
      mutation: CHANGE_PASSWORD,
      apolloNamespace: ApolloNamespace.uaa,
      variables: { input: body },
    });
  }
}
